import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import {
  Sparkles, Wand2, Loader2, Send, Image as ImageIcon, Video, Library, Users, Cpu,
  DollarSign, Settings2, ChevronDown, Download, Globe, Lock, Check, X, RefreshCw,
  Square, Smartphone, Monitor, Tv, RectangleHorizontal, Upload, Clock, Maximize2, Brush, Film, Search,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import VideoThumbnail from '../components/VideoThumbnail';
import PresetThumb from '../components/PresetThumb';
import { formatCredits } from '../utils/credits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ASPECT_RATIOS = [
  { id: '1:1', label: 'Square', icon: Square },
  { id: '16:9', label: 'Landscape', icon: Monitor },
  { id: '9:16', label: 'Portrait', icon: Smartphone },
  { id: '4:3', label: 'Classic', icon: Tv },
  { id: '3:4', label: 'Tall', icon: RectangleHorizontal },
];

const NUM_IMAGES = [1, 2, 4];

const NAV_ITEMS = [
  { id: 'generate', label: 'Generate', icon: Sparkles, path: '/generate' },
  { id: 'canvas', label: 'Canvas', icon: Brush, path: '/canvas' },
  { id: 'image-to-video', label: 'Img → Vid', icon: Film, path: '/image-to-video' },
  { id: 'library', label: 'Library', icon: Library, path: '/library' },
  { id: 'community', label: 'Community', icon: Users, path: '/community' },
];

export default function GenerateStudio() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const promptRef = useRef(null);

  // Models, presets, generation state
  const [models, setModels] = useState([]);
  const [imagePresets, setImagePresets] = useState([]);
  const [videoPresets, setVideoPresets] = useState([]);
  const [workerStats, setWorkerStats] = useState(null);

  // Form state
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [selectedModel, setSelectedModel] = useState('gpt-image-1');
  const [aspect, setAspect] = useState('1:1');
  const [numImages, setNumImages] = useState(1);
  const [duration, setDuration] = useState(4);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showPresetGallery, setShowPresetGallery] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [initImagePath, setInitImagePath] = useState(null);
  const [initImagePreview, setInitImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Generation state
  const [generating, setGenerating] = useState(false);
  const [enhancing, setEnhancing] = useState(false);
  const [recentGenerations, setRecentGenerations] = useState([]);
  const [activeJobs, setActiveJobs] = useState([]);  // in-progress server-side jobs (Sora 2)

  const model = models.find((m) => m.id === selectedModel) || models[0];
  const isVideo = model?.kind === 'video';
  const currentPresets = isVideo ? videoPresets : imagePresets;
  const categories = ['All', ...Array.from(new Set(currentPresets.map((p) => p.category)))];
  const filteredPresets = activeCategory === 'All' ? currentPresets : currentPresets.filter((p) => p.category === activeCategory);

  const fetchLibrary = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/library?limit=24`, { withCredentials: true });
      setRecentGenerations(res.data || []);
    } catch (e) {
      // 401 (not logged in) is expected; only log unexpected errors
      if (e?.response?.status && e.response.status !== 401) {
        console.warn('[generate] library fetch failed', e.message);
      }
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/workers/online-count`);
      setWorkerStats(res.data);
    } catch (e) {
      console.warn('[generate] worker stats failed', e?.message);
    }
  }, []);

  useEffect(() => {
    axios.get(`${API}/models`).then((r) => setModels(r.data || []));
    axios.get(`${API}/presets`).then((r) => {
      setImagePresets(r.data.image || []);
      setVideoPresets(r.data.video || []);
    });
    fetchLibrary();
    fetchStats();
    const interval = setInterval(fetchStats, 15000);
    return () => clearInterval(interval);
  }, [fetchLibrary, fetchStats]);

  // Live polling for in-progress jobs (every 2.5s)
  useEffect(() => {
    if (activeJobs.length === 0) return;
    const intervalId = setInterval(async () => {
      const updates = await Promise.all(
        activeJobs.map((j) =>
          axios.get(`${API}/jobs/${j.job_id}`, { withCredentials: true })
            .then((r) => r.data)
            .catch(() => null)
        )
      );
      const stillActive = [];
      let anyCompleted = false;
      let anyFailed = false;
      updates.forEach((u, idx) => {
        if (!u) { stillActive.push(activeJobs[idx]); return; }
        if (u.status === 'completed') {
          anyCompleted = true;
        } else if (u.status === 'failed') {
          anyFailed = true;
        } else {
          stillActive.push({ ...activeJobs[idx], progress: u.progress || activeJobs[idx].progress, status: u.status });
        }
      });
      setActiveJobs(stillActive);
      if (anyCompleted) {
        toast.success('Your video is ready! 🎬');
        fetchLibrary();
      }
      if (anyFailed) {
        toast.error('Video generation failed — credits refunded');
        fetchLibrary();
      }
    }, 2500);
    return () => clearInterval(intervalId);
  }, [activeJobs, fetchLibrary]);

  // Load remix data when ?remix=<jobId> is present in URL
  useEffect(() => {
    const remixId = searchParams.get('remix');
    const sketchPath = searchParams.get('sketch_path');
    const sketchPrompt = searchParams.get('prompt');

    // Handoff from sketch canvas
    if (sketchPath) {
      setInitImagePath(sketchPath);
      setInitImagePreview(`${API}/files/${sketchPath}`);
      if (sketchPrompt) setPrompt(sketchPrompt);
      // Auto-switch to FLUX.1 Dev for true sketch-to-image
      setSelectedModel('flux-dev');
      toast.success('Sketch loaded — pick details & generate ✨');
      searchParams.delete('sketch_path');
      searchParams.delete('prompt');
      setSearchParams(searchParams, { replace: true });
      return;
    }

    if (!remixId) return;
    axios.get(`${API}/jobs/${remixId}/remix`, { withCredentials: true }).then((r) => {
      const d = r.data;
      // Pre-populate from remixed job
      setPrompt(d.original_prompt || d.title || '');
      const cfg = d.config || {};
      if (cfg.aspect_ratio) setAspect(cfg.aspect_ratio);
      if (cfg.model) setSelectedModel(cfg.model);
      if (cfg.negative_prompt) setNegativePrompt(cfg.negative_prompt);
      if (cfg.num_images && cfg.num_images >= 1 && cfg.num_images <= 4) setNumImages(cfg.num_images);
      if (cfg.duration) setDuration(cfg.duration);
      if (cfg.preset_id) {
        // Find the preset object — wait for presets to be loaded
        const allPresets = [...imagePresets, ...videoPresets];
        const p = allPresets.find((x) => x.id === cfg.preset_id);
        if (p) setSelectedPreset(p);
      }
      toast.success('Remix loaded — tweak & generate ✨');
      // Clear param so refresh doesn't reload
      searchParams.delete('remix');
      setSearchParams(searchParams, { replace: true });
    }).catch((err) => {
      toast.error(err.response?.data?.detail || 'Could not load remix');
    });
  }, [searchParams, imagePresets, videoPresets]);

  useEffect(() => {
    setActiveCategory('All');
    // Clamp num images
    if (model?.max_n && numImages > model.max_n) setNumImages(model.max_n);
  }, [selectedModel, model, numImages]);

  const handleInitImageUpload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Image must be under 10MB'); return; }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setInitImagePath(res.data.path);
      setInitImagePreview(URL.createObjectURL(file));
      toast.success('Reference image attached');
    } catch (err) {
      toast.error('Upload failed');
    } finally { setUploadingImage(false); }
  };

  const clearInitImage = () => {
    setInitImagePath(null);
    setInitImagePreview(null);
  };

  const enhancePrompt = async () => {
    if (!prompt.trim()) { toast.error('Write something first'); return; }
    setEnhancing(true);
    try {
      const res = await axios.post(`${API}/generate/enhance-prompt`, { prompt, kind: isVideo ? 'video' : 'image' }, { withCredentials: true });
      setPrompt(res.data.enhanced || prompt);
      toast.success('Prompt enhanced ✨');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Enhance failed');
    } finally { setEnhancing(false); }
  };

  const generate = async () => {
    if (!user) { navigate('/login'); return; }
    if (!prompt.trim()) { toast.error('Enter a prompt'); promptRef.current?.focus(); return; }

    setGenerating(true);
    try {
      // Sora 2 — server-side video generation
      if (selectedModel === 'sora-2') {
        const res = await axios.post(`${API}/generate/video-quick`, {
          prompt,
          model: 'sora-2',
          aspect_ratio: aspect,
          duration,
          preset_id: selectedPreset?.id,
          preset_name: selectedPreset?.name,
        }, { withCredentials: true });
        toast.success(`Sora 2 rendering started — ${duration}s clip incoming`);
        // Add to active jobs for live progress tracking
        setActiveJobs((prev) => [{
          job_id: res.data.job_id,
          service_type: 'ai_video',
          title: prompt.slice(0, 80),
          status: 'processing',
          progress: 10,
          model: 'sora-2',
          aspect_ratio: aspect,
          started_at: Date.now(),
        }, ...prev]);
      } else if (isVideo || model?.provider === 'worker') {
        // Worker-routed: queue a job
        const res = await axios.post(`${API}/jobs`, {
          service_type: isVideo ? 'ai_video' : 'ai_image',
          title: prompt.slice(0, 80),
          description: selectedPreset ? `${prompt}, ${selectedPreset.suffix}` : prompt,
          config: {
            model: selectedModel,
            aspect_ratio: aspect,
            preset_id: selectedPreset?.id,
            preset_name: selectedPreset?.name,
            negative_prompt: negativePrompt,
            init_image_path: initImagePath,
            ...(selectedPreset?.params || {}),
          },
        }, { withCredentials: true });
        toast.success(`Queued ${model?.name || ''} — rendering on GPU cluster (${model?.speed || 'a few min'})`);
        // Stay on the same page; track in activeJobs so user can see live progress.
        setActiveJobs((prev) => [...prev, { job_id: res.data.job_id, status: 'queued', progress: 0, model: selectedModel, model_name: model?.name, kind: model?.kind, started_at: Date.now() }]);
      } else {
        // Server-side instant generation (gpt-image-1)
        const res = await axios.post(`${API}/generate/quick`, {
          prompt,
          model: selectedModel,
          aspect_ratio: aspect,
          num_images: numImages,
          negative_prompt: negativePrompt,
          preset_id: selectedPreset?.id,
          preset_name: selectedPreset?.name,
          init_image_path: initImagePath,
        }, { withCredentials: true });
        toast.success(`Generated ${res.data.image_paths.length} image${res.data.image_paths.length > 1 ? 's' : ''} ✨`);
        fetchLibrary();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Generation failed');
    } finally { setGenerating(false); }
  };

  const submitOnEnter = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); generate(); }
  };

  const publishToCommunity = async (jobId, currentPublic) => {
    try {
      await axios.patch(`${API}/library/${jobId}/publish`, { is_public: !currentPublic }, { withCredentials: true });
      toast.success(!currentPublic ? 'Published to community 🎨' : 'Made private');
      fetchLibrary();
    } catch (err) { toast.error('Failed'); }
  };

  const downloadImage = (path) => {
    const a = document.createElement('a');
    a.href = `${API}/files/${path}`;
    a.download = path.split('/').pop();
    document.body.appendChild(a); a.click(); a.remove();
  };

  const remix = (jobId) => {
    navigate(`/generate?remix=${jobId}`);
  };

  const upscale = async (jobId, factor = 2) => {
    try {
      toast.loading(`Upscaling ${factor}×…`, { id: `upscale-${jobId}` });
      const res = await axios.post(`${API}/upscale`, { job_id: jobId, factor }, { withCredentials: true });
      toast.success(`Upscaled to ${factor}× ✨`, { id: `upscale-${jobId}` });
      fetchLibrary();
      return res.data;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upscale failed', { id: `upscale-${jobId}` });
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0F] text-white flex" data-testid="generate-studio">
      {/* ── Left Rail ── */}
      <aside className="w-[68px] lg:w-[200px] border-r border-white/[0.05] bg-[#08080C] flex flex-col">
        <Link to="/" className="h-16 flex items-center justify-center lg:justify-start lg:px-5 border-b border-white/[0.05]">
          <span className="font-body text-xl font-extrabold tracking-tight text-white">D<span className="text-[#FF6B4A]">2</span><span className="hidden lg:inline">ORT</span></span>
        </Link>
        <nav className="flex-1 py-4 px-2 lg:px-3 space-y-1">
          {NAV_ITEMS.map((it) => {
            const active = location.pathname === it.path;
            return (
              <Link key={it.id} to={it.path} data-testid={`nav-${it.id}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium transition-colors ${active ? 'bg-[#FF6B4A]/15 text-[#FF6B4A]' : 'text-white/60 hover:text-white hover:bg-white/[0.04]'}`}>
                <it.icon className="w-4 h-4 shrink-0" />
                <span className="hidden lg:inline">{it.label}</span>
              </Link>
            );
          })}
          <div className="pt-4 mt-4 border-t border-white/[0.05] space-y-1">
            <Link to="/models" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium text-[#FFD93D] hover:bg-[#FFD93D]/[0.06]" data-testid="nav-models">
              <Cpu className="w-4 h-4 shrink-0" />
              <span className="hidden lg:inline">150 Models</span>
            </Link>
            <Link to="/pricing" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-xs font-medium text-white/60 hover:text-white hover:bg-white/[0.04]" data-testid="nav-credits-link">
              <Sparkles className="w-4 h-4 shrink-0" />
              <span className="hidden lg:inline">Pricing</span>
            </Link>
          </div>
        </nav>
        {/* Models teaser widget */}
        <div className="hidden lg:block mx-3 mb-4 p-3 rounded-xl bg-gradient-to-br from-[#FFD93D]/15 to-[#FF6B4A]/15 border border-[#FFD93D]/30" data-testid="models-widget">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles className="w-3 h-3 text-[#FFD93D]" />
            <span className="text-[10px] font-semibold tracking-wider text-[#FFD93D] uppercase">150 AI Models</span>
          </div>
          <div className="font-display text-lg font-bold text-white leading-tight">All Unlocked</div>
          <div className="text-[10px] text-white/50">DALL·E · FLUX · Sora · Kling…</div>
          <Link to="/models" className="block mt-2 text-[10px] font-semibold text-[#FFD93D] hover:text-[#FFE066]" data-testid="models-widget-cta">Explore →</Link>
        </div>
        {user && (
          <div className="hidden lg:flex items-center gap-2 px-3 pb-4 text-white/60">
            {user.picture ? <img src={user.picture} alt="" className="w-6 h-6 rounded-full" /> : <div className="w-6 h-6 rounded-full bg-[#FF6B4A]/30" />}
            <div className="flex-1 min-w-0">
              <div className="text-[11px] text-white truncate">{user.name?.split(' ')[0]}</div>
              <div className="text-[9px] text-white/50">{formatCredits(user)} cr</div>
            </div>
          </div>
        )}
      </aside>

      {/* ── Main canvas ── */}
      <main className="flex-1 flex flex-col h-screen">
        {/* Top bar: model + aspect + count */}
        <div className="relative z-30 h-16 border-b border-white/[0.05] flex items-center gap-3 px-5 lg:px-8 bg-[#0A0A0F] shrink-0 overflow-visible">
          <ModelPicker models={models} selected={selectedModel} onChange={setSelectedModel} />
          <div className="w-px h-6 bg-white/[0.05]" />
          <div className="flex items-center gap-1">
            {ASPECT_RATIOS.map((ar) => (
              <button key={ar.id} type="button" onClick={() => setAspect(ar.id)}
                data-testid={`aspect-${ar.id}`}
                title={ar.label}
                className={`w-8 h-8 rounded-md flex items-center justify-center transition-colors ${aspect === ar.id ? 'bg-[#FF6B4A]/20 text-[#FF6B4A]' : 'text-white/40 hover:text-white hover:bg-white/[0.05]'}`}>
                <ar.icon className="w-3.5 h-3.5" />
              </button>
            ))}
          </div>
          {!isVideo && (
            <>
              <div className="w-px h-6 bg-white/[0.05]" />
              <div className="flex items-center gap-1">
                {NUM_IMAGES.map((n) => (
                  <button key={n} type="button" onClick={() => setNumImages(n)}
                    disabled={model?.max_n && n > model.max_n}
                    data-testid={`num-${n}`}
                    className={`w-8 h-8 rounded-md text-xs font-bold transition-colors disabled:opacity-20 ${numImages === n ? 'bg-[#FF6B4A]/20 text-[#FF6B4A]' : 'text-white/40 hover:text-white hover:bg-white/[0.05]'}`}>
                    {n}
                  </button>
                ))}
              </div>
            </>
          )}
          {isVideo && selectedModel === 'sora-2' && (
            <>
              <div className="w-px h-6 bg-white/[0.05]" />
              <div className="flex items-center gap-1" data-testid="duration-selector">
                {[4, 8, 12].map((d) => (
                  <button key={d} type="button" onClick={() => setDuration(d)}
                    data-testid={`dur-${d}`}
                    className={`h-8 px-2.5 rounded-md text-[11px] font-bold transition-colors ${duration === d ? 'bg-[#E040A0]/20 text-[#E040A0]' : 'text-white/40 hover:text-white hover:bg-white/[0.05]'}`}>
                    {d}s
                  </button>
                ))}
              </div>
            </>
          )}
          <div className="flex-1" />
          <button type="button" onClick={() => setShowAdvanced(!showAdvanced)}
            data-testid="toggle-advanced"
            className="flex items-center gap-1.5 px-3 h-8 rounded-md text-[11px] text-white/60 hover:text-white hover:bg-white/[0.05]">
            <Settings2 className="w-3.5 h-3.5" /> Advanced
          </button>
        </div>

        {/* Generation area + preset rail */}
        <div className="flex-1 flex overflow-hidden">
          {/* Center: gallery */}
          <div className="flex-1 overflow-y-auto p-5 lg:p-8" data-testid="generation-grid">
            <div className="max-w-5xl mx-auto">
              {/* In-progress jobs (Sora 2 etc.) */}
              {activeJobs.length > 0 && (
                <div className="mb-6" data-testid="active-jobs">
                  <h2 className="font-display text-base font-bold text-white mb-3 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-[#E040A0]" />
                    Rendering now
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {activeJobs.map((j) => <InProgressCard key={j.job_id} job={j} />)}
                  </div>
                </div>
              )}
              {recentGenerations.length === 0 && activeJobs.length === 0 ? (
                <EmptyState model={model} />
              ) : recentGenerations.length > 0 && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-display text-xl font-bold text-white">Your latest creations</h2>
                    <Link to="/library" className="text-xs text-white/50 hover:text-[#FF6B4A]">View all →</Link>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {recentGenerations.map((job) => (
                      <GenerationCard key={job.job_id} job={job} onDownload={downloadImage} onPublish={publishToCommunity} onRemix={remix} onUpscale={upscale} />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Right: preset rail + image-to-image upload */}
          {showPresetGallery && (
            <aside className="w-80 border-l border-white/[0.05] bg-[#08080C] overflow-y-auto hidden lg:flex flex-col" data-testid="preset-rail">
              {/* Image-to-image upload */}
              <div className="p-4 border-b border-white/[0.05]" data-testid="i2i-upload-section">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-white/80 tracking-wider uppercase">Reference Image</h3>
                  {initImagePath && <button onClick={clearInitImage} className="text-[10px] text-white/40 hover:text-[#FF6B4A]">Clear</button>}
                </div>
                {initImagePreview ? (
                  <div className="relative rounded-lg overflow-hidden border border-white/[0.08]" data-testid="i2i-preview">
                    <img src={initImagePreview} alt="reference" className="w-full h-32 object-cover" />
                    <button onClick={clearInitImage} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center">
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ) : (
                  <label className="block cursor-pointer" data-testid="i2i-upload-label">
                    <div className="border-2 border-dashed border-white/[0.08] rounded-lg p-4 text-center hover:border-[#FF6B4A]/40 hover:bg-white/[0.02] transition-colors">
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleInitImageUpload(e.target.files[0])} data-testid="i2i-file-input" />
                      {uploadingImage ? (
                        <Loader2 className="w-5 h-5 text-white/40 animate-spin mx-auto" />
                      ) : (
                        <>
                          <Upload className="w-5 h-5 text-white/40 mx-auto mb-1" />
                          <div className="text-[10px] text-white/60">Drop or click to upload</div>
                          <div className="text-[9px] text-white/30 mt-0.5">For image-to-image · max 10MB</div>
                        </>
                      )}
                    </div>
                  </label>
                )}
                {initImagePath && model?.provider === 'server' && model?.kind === 'image' && (
                  <p className="text-[9px] text-[#FFD93D] mt-2">⚠ Server-side model uses prompt only. Switch to FLUX.1 Dev for true image-to-image.</p>
                )}
              </div>

              <div className="p-4 border-b border-white/[0.05]">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-semibold text-white/80 tracking-wider uppercase">Style Presets</h3>
                  <button type="button" onClick={() => setShowPresetGallery(false)} className="text-white/40 hover:text-white">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-1">
                  {categories.slice(0, 8).map((cat) => (
                    <button key={cat} type="button" onClick={() => setActiveCategory(cat)}
                      data-testid={`cat-${cat}`}
                      className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${activeCategory === cat ? 'bg-[#FF6B4A] text-white' : 'bg-white/[0.05] text-white/60 hover:bg-white/[0.1]'}`}>
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 p-3">
                {filteredPresets.map((p) => {
                  const isSelected = selectedPreset?.id === p.id;
                  return (
                    <button key={p.id} type="button" onClick={() => setSelectedPreset(isSelected ? null : p)}
                      data-testid={`preset-${p.id}`}
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all hover:scale-[1.02] ${isSelected ? 'border-[#FF6B4A]' : 'border-transparent'}`}>
                      <PresetThumb preset={p} selected={isSelected} />
                      {isSelected && (
                        <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-[#FF6B4A] flex items-center justify-center z-10">
                          <Check className="w-2.5 h-2.5 text-white" />
                        </div>
                      )}
                      <div className="absolute inset-x-0 bottom-0 p-1.5 bg-gradient-to-t from-black/80 to-transparent z-10">
                        <div className="text-[10px] font-semibold text-white leading-tight">{p.name}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </aside>
          )}
        </div>

        {/* Advanced panel */}
        {showAdvanced && (
          <div className="border-t border-white/[0.05] bg-[#0C0C12] p-4 lg:px-8" data-testid="advanced-panel">
            <div className="max-w-2xl">
              <label className="text-[10px] font-semibold text-white/60 tracking-wider uppercase block mb-1.5">Negative Prompt</label>
              <input type="text" value={negativePrompt} onChange={(e) => setNegativePrompt(e.target.value)}
                placeholder="What to avoid (blurry, low quality, ...)"
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-md px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF6B4A]/40"
                data-testid="negative-prompt-input" />
            </div>
          </div>
        )}

        {/* Prompt bar */}
        <div className="border-t border-white/[0.05] bg-[#08080C] p-4 lg:p-6 shrink-0">
          <div className="max-w-4xl mx-auto">
            {selectedPreset && (
              <div className="flex items-center gap-2 mb-2 text-[10px]">
                <span className="text-white/40">Style:</span>
                <span className="px-2 py-0.5 rounded-full bg-[#FF6B4A]/20 text-[#FF6B4A] font-semibold flex items-center gap-1">
                  {selectedPreset.name}
                  <button onClick={() => setSelectedPreset(null)}><X className="w-3 h-3" /></button>
                </span>
              </div>
            )}
            <div className="flex items-end gap-2">
              <div className="flex-1 relative group bg-white/[0.04] border border-white/[0.08] rounded-2xl p-3 focus-within:border-[#FF6B4A]/60 focus-within:shadow-[0_0_24px_-4px_rgba(255,107,74,0.35)] transition-all">
                {/* Animated focus glow */}
                <div className="absolute -inset-px rounded-2xl opacity-0 group-focus-within:opacity-100 transition-opacity pointer-events-none"
                  style={{
                    background: 'conic-gradient(from var(--ang, 0deg), #FF6B4A, #E040A0, #A3FF00, #4A7CFF, #FF6B4A)',
                    filter: 'blur(8px)',
                    animation: 'border-spin 4s linear infinite',
                  }}
                />
                <textarea
                  ref={promptRef}
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  onKeyDown={submitOnEnter}
                  placeholder={isVideo ? 'A majestic eagle soaring over snow-capped peaks at golden hour…' : 'A cozy reading nook by a rain-streaked window, soft warm light, watercolor style…'}
                  rows={2}
                  className="w-full bg-transparent text-white text-sm placeholder:text-white/30 focus:outline-none resize-none leading-relaxed relative z-10"
                  data-testid="prompt-input"
                />
                <div className="flex items-center justify-between mt-1">
                  <div className="flex items-center gap-2 text-[10px] text-white/40">
                    <span>{model?.speed || ''}</span>
                    <span>·</span>
                    <span>{selectedModel === 'sora-2' ? (duration === 4 ? 25 : duration === 8 ? 40 : 55) : (isVideo ? 35 : (5 * numImages))} credits</span>
                  </div>
                  <button type="button" onClick={enhancePrompt} disabled={enhancing || !prompt.trim()}
                    data-testid="enhance-btn"
                    className="text-[10px] text-white/50 hover:text-[#E040A0] disabled:opacity-30 flex items-center gap-1 transition-colors">
                    {enhancing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                    Enhance
                  </button>
                </div>
              </div>
              <motion.div
                whileTap={{ scale: 0.95 }}
                animate={generating ? { boxShadow: ['0 0 0 0 rgba(255,107,74,0.6)', '0 0 0 24px rgba(255,107,74,0)', '0 0 0 0 rgba(255,107,74,0)'] } : {}}
                transition={generating ? { duration: 1.2, repeat: Infinity, ease: 'easeOut' } : {}}
                className="rounded-2xl"
              >
                <Button onClick={generate} disabled={generating || !prompt.trim()}
                  data-testid="generate-btn"
                  className="h-14 w-14 lg:w-auto lg:px-6 bg-gradient-to-br from-[#FF6B4A] to-[#E040A0] text-white font-semibold rounded-2xl hover:from-[#FF8E72] hover:to-[#FF6B9D] disabled:opacity-40 shadow-lg shadow-[#FF6B4A]/40 transition-colors">
                  {generating ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Send className="w-4 h-4 lg:mr-1.5" /><span className="hidden lg:inline">Generate</span></>}
                </Button>
              </motion.div>
            </div>
            {!showPresetGallery && (
              <button onClick={() => setShowPresetGallery(true)} className="mt-2 text-[10px] text-white/40 hover:text-white">+ Show preset gallery</button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function ModelPicker({ models, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const sel = models.find((m) => m.id === selected) || models[0];
  if (!sel) return null;
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(!open)}
        data-testid="model-picker"
        className="flex items-center gap-2 h-9 px-3 rounded-md bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.05]">
        {sel.kind === 'video' ? <Video className="w-3.5 h-3.5 text-[#E040A0]" /> : <ImageIcon className="w-3.5 h-3.5 text-[#FF6B4A]" />}
        <span className="text-xs font-semibold text-white">{sel.name}</span>
        <span className="text-[10px] text-white/40">· {sel.speed}</span>
        <ChevronDown className="w-3 h-3 text-white/40" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div className="absolute top-11 left-0 z-[70] w-72 bg-[#0F0F15] border border-white/[0.1] rounded-lg shadow-2xl p-1.5">
            {models.map((m) => (
              <button key={m.id} type="button" onClick={() => { onChange(m.id); setOpen(false); }}
                data-testid={`model-option-${m.id}`}
                className={`w-full text-left p-2.5 rounded-md hover:bg-white/[0.05] ${selected === m.id ? 'bg-[#FF6B4A]/10' : ''}`}>
                <div className="flex items-center gap-2 mb-0.5">
                  {m.kind === 'video' ? <Video className="w-3.5 h-3.5 text-[#E040A0]" /> : <ImageIcon className="w-3.5 h-3.5 text-[#FF6B4A]" />}
                  <span className="text-xs font-semibold text-white">{m.name}</span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-white/[0.05] text-white/50">{m.speed}</span>
                </div>
                <div className="text-[10px] text-white/40 leading-relaxed">{m.desc}</div>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function EmptyState({ model }) {
  const isVideo = model?.kind === 'video';
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center" data-testid="empty-state">
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FF6B4A]/20 to-[#E040A0]/20 flex items-center justify-center mb-4">
        {isVideo ? <Video className="w-7 h-7 text-[#E040A0]" /> : <Sparkles className="w-7 h-7 text-[#FF6B4A]" />}
      </div>
      <h2 className="font-display text-2xl font-bold text-white mb-2">Create something incredible</h2>
      <p className="text-sm text-white/50 max-w-md leading-relaxed">
        {isVideo
          ? 'Describe a scene below and CogVideoX-5b will render it on our GPU network. Pick a preset for instant style.'
          : 'Type your idea below. Hit Generate for instant cloud rendering, or pick a worker model for top-tier quality.'}
      </p>
      <div className="flex items-center gap-2 mt-6 text-[10px] text-white/40">
        <span>Pro tip:</span>
        <kbd className="px-1.5 py-0.5 rounded bg-white/[0.06] border border-white/[0.08] font-mono">⌘ Enter</kbd>
        <span>to generate</span>
      </div>
    </div>
  );
}

function GenerationCard({ job, onDownload, onPublish, onRemix, onUpscale }) {
  const [hover, setHover] = useState(false);
  const cardRef = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rX = useSpring(useTransform(my, [0, 1], [4, -4]), { damping: 26, stiffness: 200 });
  const rY = useSpring(useTransform(mx, [0, 1], [-6, 6]), { damping: 26, stiffness: 200 });
  const handleMove = (e) => {
    const r = cardRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const handleLeave = () => { mx.set(0.5); my.set(0.5); setHover(false); };

  const images = job.image_paths && job.image_paths.length > 0 ? job.image_paths : (job.output_path ? [job.output_path] : []);
  if (images.length === 0) return null;
  const isVideo = job.service_type === 'ai_video' || job.service_type === 'video_render';
  return (
    <motion.div
      ref={cardRef}
      onMouseMove={handleMove}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={handleLeave}
      initial={{ opacity: 0, y: 22, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
      style={{ rotateX: rX, rotateY: rY, transformStyle: 'preserve-3d', transformPerspective: 1200 }}
      className="group relative rounded-lg overflow-hidden bg-white/[0.03] border border-white/[0.05] hover:border-white/[0.2] aspect-square"
      data-testid={`gen-card-${job.job_id}`}
    >
      {/* Scan-reveal on first mount */}
      <motion.div
        initial={{ scaleY: 1 }} animate={{ scaleY: 0 }}
        transition={{ duration: 0.9, delay: 0.1, ease: [0.83, 0, 0.17, 1] }}
        style={{ transformOrigin: 'top' }}
        className="absolute inset-0 bg-[#FF6B4A] z-10 pointer-events-none"
      />

      <motion.div animate={{ scale: hover ? 1.06 : 1 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }} className="absolute inset-0">
        {isVideo ? (
          <VideoThumbnail path={images[0]} title={job.title} className="w-full h-full" />
        ) : (
          <img src={`${API}/files/${images[0]}`} alt={job.title} className="w-full h-full object-cover" loading="lazy"
            onError={(e) => { e.target.style.display = 'none'; }} />
        )}
      </motion.div>

      {/* Sheen */}
      <div className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(115deg, transparent 38%, rgba(255,255,255,0.18) 50%, transparent 62%)',
          transform: hover ? 'translateX(110%)' : 'translateX(-110%)',
          transition: 'transform 0.85s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      />

      {/* Glitch flash */}
      {hover && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: [0, 0.3, 0.05, 0.3, 0.05] }}
          transition={{ duration: 0.5, times: [0, 0.2, 0.4, 0.6, 1] }}
          className="absolute inset-0 mix-blend-overlay pointer-events-none"
          style={{ background: 'repeating-linear-gradient(90deg, rgba(255,107,74,0.4) 0px, transparent 2px, transparent 4px)' }}
        />
      )}
      {hover && (
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent flex flex-col justify-end p-2.5">
          <div className="text-[10px] text-white/80 line-clamp-2 mb-2">{job.title}</div>
          <div className="flex items-center gap-1 flex-wrap">
            <button onClick={() => onRemix(job.job_id)} title="Remix" data-testid={`remix-${job.job_id}`}
              className="w-7 h-7 rounded-md bg-[#FF6B4A]/30 hover:bg-[#FF6B4A]/50 flex items-center justify-center">
              <RefreshCw className="w-3.5 h-3.5 text-[#FF6B4A]" />
            </button>
            {!isVideo && (
              <button onClick={() => onUpscale(job.job_id, 2)} title="Upscale 2×" data-testid={`upscale-${job.job_id}`}
                className="w-7 h-7 rounded-md bg-[#A855F7]/30 hover:bg-[#A855F7]/50 flex items-center justify-center">
                <Maximize2 className="w-3.5 h-3.5 text-[#A855F7]" />
              </button>
            )}
            <button onClick={() => onDownload(images[0])} title="Download" data-testid={`download-${job.job_id}`}
              className="w-7 h-7 rounded-md bg-white/10 hover:bg-white/20 flex items-center justify-center">
              <Download className="w-3.5 h-3.5 text-white" />
            </button>
            <button onClick={() => onPublish(job.job_id, job.is_public)} title={job.is_public ? 'Unpublish' : 'Publish'}
              data-testid={`publish-${job.job_id}`}
              className={`w-7 h-7 rounded-md flex items-center justify-center ${job.is_public ? 'bg-[#34B76F]/30 hover:bg-[#34B76F]/40' : 'bg-white/10 hover:bg-white/20'}`}>
              {job.is_public ? <Globe className="w-3.5 h-3.5 text-[#34B76F]" /> : <Lock className="w-3.5 h-3.5 text-white" />}
            </button>
          </div>
        </div>
      )}
      {job.is_public && (
        <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-[#34B76F]/80 flex items-center justify-center">
          <Globe className="w-2.5 h-2.5 text-white" />
        </div>
      )}
    </motion.div>
  );
}


function InProgressCard({ job }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const elapsed = Math.floor((now - job.started_at) / 1000);
  const isVideo = job.kind === 'video' || job.service_type === 'ai_video';
  return (
    <div className="relative rounded-lg overflow-hidden border border-[#E040A0]/30 bg-gradient-to-br from-[#E040A0]/10 to-[#4A7CFF]/10 aspect-square" data-testid={`progress-${job.job_id}`}>
      <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
        <div className="w-12 h-12 rounded-2xl bg-white/[0.06] border border-white/[0.1] flex items-center justify-center mb-3 backdrop-blur">
          {isVideo ? <Video className="w-5 h-5 text-[#E040A0]" /> : <ImageIcon className="w-5 h-5 text-[#FF6B4A]" />}
        </div>
        <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#E040A0] mb-1.5">
          {job.model === 'sora-2' ? 'Sora 2' : (job.model || 'Rendering')}
        </div>
        <div className="text-[10px] text-white/70 line-clamp-2 mb-3 px-1">{job.title}</div>
        <div className="w-full h-1.5 rounded-full bg-white/[0.06] overflow-hidden mb-2">
          <div
            className="h-full bg-gradient-to-r from-[#E040A0] to-[#4A7CFF] transition-all duration-500"
            style={{ width: `${job.progress || 10}%` }}
          />
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-white/40 font-mono">
          <Clock className="w-2.5 h-2.5" /> {elapsed}s · {job.progress || 10}%
        </div>
      </div>
    </div>
  );
}
